import React, { createContext, useContext, useEffect, useRef } from "react";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";

import { type SignupUFOExperience } from "@/components/signup/utils/signup-ufo-logging";
import { ufoEventProvisioningFunnel } from "@/components/signup/utils/ufo-events";
import {
    UfoCustomTimingKey,
    type SignupUfoCustomData
} from "@/components/signup/utils/ufo.types";
import { BlockingRedirectPromise } from "@/templates/ondemand/signup/confirmation/confirmation-page/src/hooks/use-cloud-status-redirection";

export const PROVISIONING_FUNNEL_TIMING_KEYS = [
    UfoCustomTimingKey.PERCEIVED_PROVISIONING_FUNNEL_TENANT_ID_RESOLUTION,
    UfoCustomTimingKey.PERCEIVED_PROVISIONING_FUNNEL_PROVISIONING_SUCCESS,
    UfoCustomTimingKey.PERCEIVED_PROVISIONING_FUNNEL_PROJECT_CREATION_INITIALIZED,
    UfoCustomTimingKey.PERCEIVED_PROVISIONING_FUNNEL_INVITE_TEAMMATES
] as const;

export type ProvisioningFunnelTimingKey =
    (typeof PROVISIONING_FUNNEL_TIMING_KEYS)[number];

type ProvisioningFunnelUfoContextValue = {
    ufoEvent?: SignupUFOExperience;
};

const ProvisioningFunnelUfoContext =
    createContext<ProvisioningFunnelUfoContextValue | null>(null);

export type ProvisioningFunnelUfoProviderProps = {
    children: React.ReactNode;
    initialCustomData?: SignupUfoCustomData;
    addBlockingRedirectPromise: (promise: BlockingRedirectPromise) => void;
};

export const ProvisioningFunnelUfoProvider = ({
    children,
    initialCustomData,
    addBlockingRedirectPromise
}: ProvisioningFunnelUfoProviderProps) => {
    const funnelStarted = useRef(false);
    const { createAnalyticsEvent } = useAnalyticsEvents();

    useEffect(() => {
        if (initialCustomData) {
            ufoEventProvisioningFunnel.setCustomData(initialCustomData);
        }

        // ensure the funnel and all substeps are marked as successful before redirecting
        addBlockingRedirectPromise(async () => {
            await ufoEventProvisioningFunnel.success();
            const percievedProvisioningTimings =
                ufoEventProvisioningFunnel.getSubstepTimings();

            let totalPercievedProvisioningTiming = 0;
            const attributes: Record<string, number | string> = {};

            for (const [key, value] of Object.entries(
                percievedProvisioningTimings
            )) {
                attributes[`${key}_start`] = value.startTime;
                attributes[`${key}_duration`] = value.duration;
                totalPercievedProvisioningTiming += value.duration;
            }

            createAnalyticsEvent({
                action: "log",
                actionSubject: "percievedProvisioningFunnel",
                eventType: "track",
                attributes: {
                    ...attributes,
                    totalPercievedProvisioningTiming
                }
            }).fire();
        });

        // Start the funnel event once on mount only
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // start funnel on mount only
    if (!funnelStarted.current) {
        ufoEventProvisioningFunnel.start();
        funnelStarted.current = true;
    }

    const value: ProvisioningFunnelUfoContextValue = {
        ufoEvent: ufoEventProvisioningFunnel
    };

    return (
        <ProvisioningFunnelUfoContext.Provider value={value}>
            {children}
        </ProvisioningFunnelUfoContext.Provider>
    );
};

export function useProvisioningFunnelUfo(): ProvisioningFunnelUfoContextValue {
    const context = useContext(ProvisioningFunnelUfoContext);
    if (context === null) {
        console.warn(
            "useProvisioningFunnelUfo must be used within a ProvisioningFunnelUfoProvider"
        );
        return { ufoEvent: undefined };
    }

    return context;
}

/**
 * Tracks the percieved provisioning timing for the given UFO custom timing key. This should only be used
 * to track visual wait times during the provisioning funnel.
 * @param ufoCustomTimingKey - The UFO custom timing key to mark
 * @param customData - Optional custom data to set on the UFO event
 * @returns A React node to render the percieved provisioning timing
 */
export const PercievedProvisioningTiming = ({
    provisioningFunnelTimingKey,
    customData
}: {
    provisioningFunnelTimingKey: ProvisioningFunnelTimingKey;
    customData?: SignupUfoCustomData;
}) => {
    const { ufoEvent } = useProvisioningFunnelUfo();

    useEffect(() => {
        if (ufoEvent && customData) {
            ufoEvent.setCustomData(customData);
        }
    }, [ufoEvent, customData]);

    useEffect(() => {
        if (ufoEvent === undefined) {
            return;
        }

        ufoEvent.startSubstep(provisioningFunnelTimingKey);

        return () => {
            ufoEvent.endSubstep(provisioningFunnelTimingKey);
        };
    }, [provisioningFunnelTimingKey]);

    return <></>;
};
